import { FC, useMemo, useState } from 'react'

import { Box, Typography, Autocomplete, TextField } from '@mui/material'

import { StatisticChart, StatisticChartsResponse } from 'services/dashboard/types'

import ChartCard from '../ChartCard'

interface StatisticChartsProps {
  data: StatisticChartsResponse | undefined
  isLoading: boolean
}

export interface ProcessedChart {
  title: string
  series: {
    id: string
    label: string
    data: number[]
    showMark: boolean
    connectNulls: boolean
  }[]
  xAxis: {
    data: Date[]
  }
}

const StatisticCharts: FC<StatisticChartsProps> = ({ data, isLoading }) => {
  const [activeNodeGroup, setActiveNodeGroup] = useState(0)

  const processChart = (chart: StatisticChart): ProcessedChart | null => {
    if (!chart?.data?.length) return null

    const validPoints = chart.data.filter((point) => {
      try {
        return point.statTime && !isNaN(new Date(point.statTime).getTime()) && point?.items
      } catch {
        return false
      }
    })

    if (!validPoints.length) return null

    const firstPoint = validPoints[0]
    const timeData = validPoints.map((point) => new Date(point.statTime))

    return {
      title: chart.title,
      series: Object.entries(firstPoint.items).map(([key]) => ({
        id: key,
        label: key,
        data: validPoints.map((point) => point.items?.[key] || 0),
        showMark: false,
        connectNulls: true,
      })),
      xAxis: { data: timeData },
    }
  }

  const { generalCharts, nodeCharts, nodeGroupOptions } = useMemo(() => {
    if (!data) return { generalCharts: [], nodeCharts: [], nodeGroupOptions: [] }

    const processedNodeCharts: ProcessedChart[][] = []
    const nodeGroupKeys = Object.keys(data.nodeCharts || {})
    
    nodeGroupKeys.forEach((key) => {
      const groupCharts = data.nodeCharts[key]
      const processedGroup = groupCharts
        .map(processChart)
        .filter((chart): chart is ProcessedChart => chart !== null)
      processedNodeCharts.push(processedGroup)
    })

    const options = nodeGroupKeys.map((key, index) => ({
      label: key,
      value: index,
    }))

    return {
      generalCharts: (data.generalCharts || [])
        .map(processChart)
        .filter((chart): chart is ProcessedChart => chart !== null),
      nodeCharts: processedNodeCharts,
      nodeGroupOptions: options,
    }
  }, [data])

  if (isLoading) {
    return (
      <Box sx={{ p: '16px' }}>
        <Typography>Loading charts...</Typography>
      </Box>
    )
  }

  if (!generalCharts.length && !nodeCharts.length) return null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {generalCharts.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: '16px', fontWeight: 600 }}>
            General Statistics
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '24px',
            }}
          >
            {generalCharts.map((chart, index) => (
              <Box
                key={chart.title}
                sx={{
                  gridColumn: generalCharts.length === 2 && index === 1 ? '2 / 4' : 'auto',
                }}
              >
                <ChartCard chart={chart} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {nodeCharts.length > 0 && (
        <Box sx={{ mb: '30px' }}>
          <Typography variant="h6" sx={{ mb: '16px', fontWeight: 600 }}>
            Node Statistics
          </Typography>

          <Autocomplete
            value={nodeGroupOptions[activeNodeGroup] || null}
            onChange={(_, newValue) => {
              setActiveNodeGroup(newValue.value)
            }}
            options={nodeGroupOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} placeholder="Select node group..." size="small" />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.label}
              </Box>
            )}
            noOptionsText="No groups found"
            sx={{ width: '500px' }}
          />

          {nodeCharts[activeNodeGroup] && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
              }}
            >
              {nodeCharts[activeNodeGroup].map((chart, chartIndex) => (
                <Box
                  key={`${activeNodeGroup}-${chartIndex}`}
                  sx={{
                    gridColumn:
                      nodeCharts[activeNodeGroup].length === 2 && chartIndex === 1
                        ? '2 / 4'
                        : 'auto',
                  }}
                >
                  <ChartCard chart={chart} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}
export default StatisticCharts
